import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor: attach Bearer token from localStorage to all API requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token && config.url?.startsWith(BACKEND_URL)) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    // Retry a transient /auth/me failure before nuking the session — a
    // single 502 / network hiccup on refresh used to log teachers out
    // permanently. Now: only clear the token on a definitive 401/403
    // (token actually invalid). For network errors / 5xx, retry up to
    // 2 more times with backoff; if STILL failing, keep the user logged
    // in but surface as null this render so guarded routes can decide.
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setLoading(false);
        return;
      } catch (error) {
        lastErr = error;
        const status = error?.response?.status;
        // Definitive auth failures → clear token and stop retrying
        if (status === 401 || status === 403) {
          localStorage.removeItem('session_token');
          setUser(null);
          setLoading(false);
          return;
        }
        // Transient — retry with backoff (400ms, 1.6s)
        if (attempt < 2) {
          await new Promise((r) => setTimeout(r, 400 * (attempt + 1) ** 2));
        }
      }
    }
    // All retries exhausted on transient failure — keep the token in
    // localStorage so the next navigation can re-attempt rather than
    // forcing the teacher through magic-link again. Mark user null so
    // guarded routes still gate, but DO NOT clear the token.
    console.warn('[auth] /me transient failure after 3 attempts:', lastErr?.message);
    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (userData, sessionToken) => {
    localStorage.setItem('session_token', sessionToken);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
